import React from 'react';
import { motion } from 'framer-motion';
import { Wind, Sun, Shield, Layers, Scissors, LayoutGrid, Ruler, Sparkles, ArrowRight } from 'lucide-react';

const services = [
  {
    Icon: Wind,
    name: 'Gardinen & Vorhänge',
    desc: 'Maßgefertigte Dekorationen für jedes Fenster — von schlicht bis opulent.',
    anchor: '#kontakt',
  },
  {
    Icon: Sun,
    name: 'Sonnenschutz',
    desc: 'Markisen, Jalousien, Plissees und Rollos — auch für Sondermaße.',
    anchor: '#kontakt',
  },
  {
    Icon: Shield,
    name: 'Insektenschutz',
    desc: 'Maßgefertigte Rahmensysteme in allen Formaten — wir messen und montieren.',
    anchor: '#kontakt',
  },
  {
    Icon: Layers,
    name: 'Polsterei',
    desc: 'Möbel beziehen, aufarbeiten, neu anfertigen — inkl. Behandlungsliegen.',
    anchor: '#kontakt',
  },
  {
    Icon: Scissors,
    name: 'Gardinen- & Polsterstoffe',
    desc: 'Großes Lager namhafter Hersteller — Beratung zu Farbe, Muster, Textur.',
    anchor: '#kontakt',
  },
  {
    Icon: LayoutGrid,
    name: 'Teppichböden',
    desc: 'Velours, Bouclé, Uni und gemustert — mit Raumluftverbesserung durch Feinstaubbindung.',
    anchor: '#kontakt',
  },
  {
    Icon: Ruler,
    name: 'Änderungsschneiderei',
    desc: 'Gardinen, Kleidung, Säume, Reißverschlüsse — alles aus einer Hand.',
    anchor: '#kontakt',
  },
  {
    Icon: Sparkles,
    name: 'Gardinenwaschservice',
    desc: 'Wir holen ab, waschen fachgerecht und hängen Ihre Gardinen wieder auf.',
    anchor: '#waschservice',
  },
];

const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.08 } },
};
const itemVariants = {
  hidden: { opacity: 0, y: 28 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } },
};

const Services = () => (
  <section
    id="leistungen"
    data-testid="services-section"
    className="py-28 bg-[#F8F5F0] linen-pattern"
  >
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <motion.div
        className="text-center mb-16"
        initial={{ opacity: 0, y: 28 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.7 }}
      >
        <span className="text-[11px] tracking-[0.22em] uppercase text-[#8B7355] font-outfit mb-3 block">
          Leistungen
        </span>
        <h2 className="font-cormorant text-4xl md:text-5xl text-[#2A2A2A] mb-5 font-medium">
          Alles aus einer Hand.
        </h2>
        <p className="font-outfit text-base text-[#2A2A2A]/65 max-w-xl mx-auto">
          Von der Beratung über die Fertigung bis zur Montage — wir begleiten jedes Projekt vollständig.
        </p>
      </motion.div>

      <motion.div
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5"
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.05 }}
      >
        {services.map((service, i) => (
          <motion.div
            key={i}
            variants={itemVariants}
            data-testid={`service-card-${i}`}
            className="bg-white border border-[#EDE8E0] p-7 group hover:-translate-y-1 hover:shadow-md transition-all duration-300 cursor-default"
          >
            <div className="w-11 h-11 bg-[#F8F5F0] border border-[#EDE8E0] flex items-center justify-center mb-5 group-hover:bg-[#8B7355] transition-colors duration-300">
              <service.Icon
                size={18}
                className="text-[#8B7355] group-hover:text-white transition-colors duration-300"
              />
            </div>
            <h3 className="font-cormorant text-xl text-[#2A2A2A] font-semibold mb-2">
              {service.name}
            </h3>
            <p className="font-outfit text-sm text-[#2A2A2A]/60 leading-relaxed mb-5">
              {service.desc}
            </p>
            <div className="border-t border-[#EDE8E0] pt-4">
              <span className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.15em] uppercase font-outfit text-[#8B7355] group-hover:gap-3 transition-all duration-300">
                Mehr erfahren
                <ArrowRight size={12} />
              </span>
            </div>
          </motion.div>
        ))}

        {/* CTA card */}
        <motion.a
          variants={itemVariants}
          href="#kontakt"
          onClick={(e) => {
            e.preventDefault();
            document.querySelector('#kontakt')?.scrollIntoView({ behavior: 'smooth' });
          }}
          data-testid="service-cta-card"
          className="bg-[#8B7355] border border-[#8B7355] p-7 flex flex-col justify-between group hover:bg-[#6B5440] transition-colors duration-300 cursor-pointer"
        >
          <div>
            <span className="text-[10px] tracking-[0.22em] uppercase font-outfit text-white/65 mb-4 block">
              Kostenlose Beratung
            </span>
            <h3 className="font-cormorant text-2xl text-white font-medium leading-snug">
              Persönliche Beratung vereinbaren
            </h3>
          </div>
          <div className="flex items-center gap-2 mt-8 text-white/80 group-hover:text-white group-hover:gap-4 transition-all duration-300">
            <span className="text-[11px] tracking-[0.15em] uppercase font-outfit">Jetzt anrufen</span>
            <ArrowRight size={13} />
          </div>
        </motion.a>
      </motion.div>
    </div>
  </section>
);

export default Services;
